/*
 * Full-width mega menu
 * - Hover intent: small open delay, forgiving close delay, quicker switching
 * - Touch (iPad landscape etc.): first tap opens, second tap follows the link
 * - Keyboard: chevron button (Enter/Space/ArrowDown) opens and focuses the panel,
 *   Tab flows through the panel and on to the next top-level item, Escape closes
 * - Preview image follows the hovered/focused link; images load lazily
 */
(() => {
  window.MEGA_MENU_VERSION = 'accordion-3';
  const DESKTOP = window.matchMedia('(min-width: 1150px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const OPEN_DELAY = 80;
  const SWITCH_DELAY = 140;
  const CLOSE_DELAY = 240;
  const HIDE_DURATION = 320;
  const FOCUSABLE = 'a[href]:not([tabindex="-1"]), button:not([disabled])';

  class MegaMenu {
    constructor(header) {
      this.header = header;
      this.section = header.closest('.shopify-section-header') || header;
      this.items = [...header.querySelectorAll('[data-mega-item]')];
      this.active = null;
      this.timer = null;
      this.warmTimer = null;
      this.pointerType = 'mouse';
      this.removedInverse = false;
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.items.forEach((item) => {
        const panel = header.querySelector(`#${CSS.escape(item.dataset.panel)}`);
        if (!panel) return;

        item.megaPanel = panel;
        item.megaLink = item.querySelector('.mega-nav__link');
        item.megaToggle = item.querySelector('.mega-nav__toggle');

        // Hover intent
        item.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.schedule(() => this.open(item), this.active ? SWITCH_DELAY : OPEN_DELAY);
        }, { signal });
        item.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.schedule(() => this.close(), CLOSE_DELAY);
        }, { signal });
        panel.addEventListener('pointerenter', (e) => {
          if (e.pointerType === 'mouse') this.cancel();
        }, { signal });
        panel.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.schedule(() => this.close(), CLOSE_DELAY);
        }, { signal });

        // Touch: first tap opens
        item.megaLink?.addEventListener('click', (e) => {
          const isTouch = this.pointerType === 'touch' || this.pointerType === 'pen';
          if (isTouch && DESKTOP.matches && this.active !== item) {
            e.preventDefault();
            this.open(item);
          }
        }, { signal });

        // Keyboard / click on chevron
        item.megaToggle?.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.active === item) {
            this.close();
            return;
          }
          this.open(item);
          if (e.detail === 0) this.focusFirst(panel); // keyboard-activated
        }, { signal });
        item.megaToggle?.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowDown') return;
          e.preventDefault();
          this.open(item);
          this.focusFirst(panel);
        }, { signal });

        // Preview follows hovered / focused link
        panel.querySelectorAll('[data-preview]').forEach((el) => {
          const show = () => this.preview(panel, el);
          el.addEventListener('pointerenter', show, { signal });
          el.addEventListener('focus', show, { signal });
        });

        panel.addEventListener('keydown', (e) => this.onPanelKeydown(e, item), { signal });

        // Accordion rows: hovering a row opens its links underneath (one open at a time).
        // Delegated on the panel so it works for every accordion inside it.
        panel.addEventListener('pointermove', (e) => {
          if (e.pointerType !== 'mouse') return;
          // Only react to real mouse movement, so rows sliding under a still cursor
          // (while another row animates closed) don't open by themselves.
          const moved = panel.megaLastX === undefined
            || Math.abs(e.clientX - panel.megaLastX) + Math.abs(e.clientY - panel.megaLastY) > 1;
          panel.megaLastX = e.clientX;
          panel.megaLastY = e.clientY;
          if (!moved) return;

          const row = e.target.closest('.mega-acc__row');
          const acc = row?.closest('[data-acc-item]');
          if (!acc) {
            if (!e.target.closest('[data-acc-item]')) this.cancelAcc(panel);
            return;
          }
          if (acc.classList.contains('is-open') || panel.megaAccPending === acc) return;
          this.scheduleAcc(panel, acc);
        }, { signal });

        panel.addEventListener('click', (e) => {
          const toggle = e.target.closest('.mega-acc__toggle');
          if (toggle) {
            e.preventDefault();
            const acc = toggle.closest('[data-acc-item]');
            if (acc.classList.contains('is-open')) this.closeAcc(panel, acc);
            else this.openAcc(panel, acc);
            return;
          }
          // Touch: first tap on a row opens it, second tap follows the link
          const link = e.target.closest('.mega-acc__link');
          const acc = link?.closest('[data-acc-item]');
          const isTouch = this.pointerType === 'touch' || this.pointerType === 'pen';
          if (acc && isTouch && !acc.classList.contains('is-open')) {
            e.preventDefault();
            this.openAcc(panel, acc);
          }
        }, { signal });
      });

      document.addEventListener('pointerdown', (e) => {
        this.pointerType = e.pointerType;
        if (!this.active) return;
        if (!e.target.closest('.mega-nav, [data-mega-panel]')) this.close();
      }, { signal });

      document.addEventListener('keydown', (e) => {
        this.pointerType = 'keyboard';
        if (e.key !== 'Escape' || !this.active) return;
        const item = this.active;
        const hadFocus = item.megaPanel.contains(document.activeElement) || item.contains(document.activeElement);
        this.close({ immediate: true });
        if (hadFocus) item.megaToggle?.focus();
      }, { signal });

      // Focus moving elsewhere in the header closes the panel
      this.header.addEventListener('focusin', (e) => {
        if (!this.active) return;
        if (this.active.contains(e.target) || this.active.megaPanel.contains(e.target)) return;
        this.close();
      }, { signal });

      // Focus leaving the header entirely
      this.header.addEventListener('focusout', (e) => {
        if (!this.active || !e.relatedTarget) return;
        if (!this.header.contains(e.relatedTarget)) this.close();
      }, { signal });

      // Close when the sticky header hides itself on scroll
      window.addEventListener('scroll', () => {
        if (this.active && this.section.classList.contains('shopify-section-header-hidden')) {
          this.close({ immediate: true });
        }
      }, { passive: true, signal });

      DESKTOP.addEventListener('change', () => {
        if (!DESKTOP.matches) this.close({ immediate: true });
      }, { signal });
    }

    schedule(fn, delay) {
      this.cancel();
      this.timer = window.setTimeout(fn, delay);
    }

    cancel() {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    open(item) {
      this.cancel();
      if (!DESKTOP.matches || this.active === item || !item.megaPanel) return;

      const switching = Boolean(this.active);
      if (this.active) this.hidePanel(this.active, true);

      this.active = item;
      const panel = item.megaPanel;
      item.classList.add('is-active');
      item.megaToggle?.setAttribute('aria-expanded', 'true');

      panel.classList.toggle('mega-panel--switch', switching);
      panel.hidden = false;
      panel.getBoundingClientRect(); // commit start state so the transition runs
      panel.classList.add('is-open');

      this.resetPreview(panel);
      panel.querySelectorAll('[data-acc-item].is-open').forEach((acc) => this.closeAcc(panel, acc));
      this.setHeaderState(true);

      // Warm the rest of this panel's preview images once the user settles
      window.clearTimeout(this.warmTimer);
      this.warmTimer = window.setTimeout(() => {
        panel.querySelectorAll('[data-preview-id]').forEach((slide) => this.loadImage(slide));
      }, 700);
    }

    close({ immediate = false } = {}) {
      this.cancel();
      window.clearTimeout(this.warmTimer);
      if (!this.active) return;
      this.hidePanel(this.active, immediate);
      this.active = null;
      this.setHeaderState(false);
    }

    hidePanel(item, immediate) {
      const panel = item.megaPanel;
      item.classList.remove('is-active');
      item.megaToggle?.setAttribute('aria-expanded', 'false');
      panel.classList.remove('is-open');

      const finish = () => {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      };
      if (immediate || REDUCED.matches) finish();
      else window.setTimeout(finish, HIDE_DURATION);
    }

    setHeaderState(open) {
      this.header.classList.toggle('mega-open', open);
      this.section.classList.toggle('mega-menu-open', open);

      // Transparent overlay header: switch to normal colors while a panel is open
      if (open && this.section.classList.contains('color-inverse')) {
        this.section.classList.remove('color-inverse');
        this.removedInverse = true;
      } else if (!open && this.removedInverse) {
        this.removedInverse = false;
        if (!this.section.classList.contains('shopify-section-header-sticky')) {
          this.section.classList.add('color-inverse');
        }
      }
    }

    resetPreview(panel) {
      const slide = panel.querySelector('[data-preview-default]') || panel.querySelector('[data-preview-id]');
      if (slide) this.activatePreview(panel, slide);
    }

    preview(panel, el) {
      const find = (id) => (id ? panel.querySelector(`[data-preview-id="${CSS.escape(id)}"]`) : null);
      const slide = find(el.dataset.preview) || find(el.dataset.previewFallback);
      if (slide) this.activatePreview(panel, slide);
    }

    activatePreview(panel, slide) {
      this.loadImage(slide);
      if (panel.megaPreview === slide) return;
      panel.megaPreview?.classList.remove('is-active');
      slide.classList.add('is-active');
      panel.megaPreview = slide;
    }

    loadImage(slide) {
      const img = slide.querySelector('img[data-src]');
      if (!img || !slide.getClientRects().length) return; // previews hidden in settings
      img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      delete img.dataset.srcset;
      delete img.dataset.src;
      if (img.complete && img.naturalWidth) img.classList.add('is-loaded');
    }

    cancelAcc(panel) {
      window.clearTimeout(panel.megaAccTimer);
      panel.megaAccPending = null;
    }

    scheduleAcc(panel, acc) {
      window.clearTimeout(panel.megaAccTimer);
      panel.megaAccPending = acc;
      // Short hover intent so sweeping past an item doesn't open it
      panel.megaAccTimer = window.setTimeout(() => {
        panel.megaAccPending = null;
        this.openAcc(panel, acc);
      }, 120);
    }

    openAcc(panel, acc) {
      window.clearTimeout(panel.megaAccTimer);
      panel.megaAccPending = null;
      panel.querySelectorAll('[data-acc-item].is-open').forEach((other) => {
        if (other !== acc) this.closeAcc(panel, other);
      });
      acc.classList.add('is-open');
      acc.querySelector('.mega-acc__toggle')?.setAttribute('aria-expanded', 'true');
      acc.closest('.mega-acc')?.classList.add('has-open');
    }

    closeAcc(panel, acc) {
      acc.classList.remove('is-open');
      acc.querySelector('.mega-acc__toggle')?.setAttribute('aria-expanded', 'false');
      const wrap = acc.closest('.mega-acc');
      if (wrap && !wrap.querySelector('[data-acc-item].is-open')) wrap.classList.remove('has-open');
    }

    focusFirst(panel) {
      window.requestAnimationFrame(() => panel.querySelector(FOCUSABLE)?.focus());
    }

    onPanelKeydown(e, item) {
      if (e.key !== 'Tab') return;
      const focusables = [...item.megaPanel.querySelectorAll(FOCUSABLE)].filter((el) =>
        el.checkVisibility ? el.checkVisibility({ visibilityProperty: true }) : el.getClientRects().length
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        const next = item.nextElementSibling?.querySelector('a[href], button');
        this.close({ immediate: true });
        (next || item.megaToggle)?.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        item.megaToggle?.focus();
      }
    }

    destroy() {
      this.close({ immediate: true });
      this.controller.abort();
    }
  }

  let instance = null;

  const init = () => {
    instance?.destroy();
    instance = null;
    const header = document.querySelector('.shopify-section-header .header');
    if (header && header.querySelector('[data-mega-item]')) instance = new MegaMenu(header);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

  // Theme editor: selecting a "Mega menu panel" block opens its panel
  document.addEventListener('shopify:block:select', (e) => {
    if (!instance) return;
    const item = instance.items.find((i) => i.megaPanel?.dataset.blockId === e.detail.blockId);
    if (item) instance.open(item);
  });
  document.addEventListener('shopify:block:deselect', () => instance?.close({ immediate: true }));
})();
